import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValueEvent } from "framer-motion";
import { Phone } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL } from "@/data/spa";
import { useLang } from "@/i18n/LanguageContext";
import { useAmbience } from "@/context/AmbienceContext";

const FRAMES = [
  {
    poster: "https://static.prod-images.emergentagent.com/jobs/ecec5624-a917-4f22-ad18-cabb450b02ee/images/c9d0cc9dea00d0689b98dd1a9e396878346f77973c585f5fdbbc89e7408c2d19.jpeg",
    video: "/arrival/1.mp4",
  },
  {
    poster: "https://static.prod-images.emergentagent.com/jobs/ecec5624-a917-4f22-ad18-cabb450b02ee/images/be81a45aa654ec12e286ce8058c187c96e1df738091e78741ec444b2772068b6.jpeg",
    video: "/arrival/2.mp4",
  },
  {
    poster: "https://static.prod-images.emergentagent.com/jobs/ecec5624-a917-4f22-ad18-cabb450b02ee/images/f4b47ceea508802038368fc6ff4e9c1bf3d013c5b970bab91c4082704332add1.jpeg",
    video: "/arrival/3.mp4",
  },
  {
    poster: "https://static.prod-images.emergentagent.com/jobs/ecec5624-a917-4f22-ad18-cabb450b02ee/images/cb9fb8db2f87fb9ff3784562281563b6bbad40db106bc35d51b70f1eac6cb6bc.jpeg",
    video: "/arrival/4.mp4",
  },
  {
    poster: "https://static.prod-images.emergentagent.com/jobs/ecec5624-a917-4f22-ad18-cabb450b02ee/images/f049937611ba955bd6157d829a5970ef35913c3fb070705d5d29a7c2bca03682.jpeg",
    video: "/arrival/5.mp4",
  },
];
export { FRAMES };

const SEG = 0.17;
const FINAL_START = 0.85;

const STEAM = Array.from({ length: 10 }, (_, i) => ({
  left: `${(i * 9.7 + 5) % 94}%`,
  size: 70 + ((i * 41) % 110),
  duration: 12 + ((i * 3) % 8),
  delay: (i * 1.9) % 12,
  opacity: 0.05 + ((i % 3) * 0.03),
  drift: i % 2 === 0 ? 55 : -45,
}));

const Frame = ({ i, frame, progress, active }) => {
  const start = i * SEG;
  const end = i === FRAMES.length - 1 ? 1 : start + SEG;
  const opacity = useTransform(
    progress,
    i === 0 ? [0, end, Math.min(end + 0.05, 1)] : [Math.max(start - 0.05, 0), start, end, Math.min(end + 0.05, 1)],
    i === 0 ? [1, 1, 0] : [0, 1, 1, 0]
  );
  const scale = useTransform(progress, [Math.max(start - 0.05, 0), Math.min(end + 0.05, 1)], [1.18, 1.02]);
  const y = useTransform(progress, [Math.max(start - 0.05, 0), Math.min(end + 0.05, 1)], ["2%", "-2%"]);
  const videoRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (active) {
      v.play().catch(() => {});
    } else {
      v.pause();
    }
  }, [active]);

  return (
    <motion.div style={{ opacity }} className="absolute inset-0">
      <motion.video
        ref={videoRef}
        src={frame.video}
        poster={frame.poster}
        muted
        loop
        playsInline
        preload={i <= 1 ? "auto" : "metadata"}
        style={{ scale, y }}
        className="w-full h-full object-contain sm:object-cover bg-[#050706] will-change-transform"
      />
      <div className="absolute inset-0 bg-black/35" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#050706]/70 via-transparent to-[#050706]/85" />
    </motion.div>
  );
};

const ChapterText = ({ i, chapter, progress }) => {
  const start = i * SEG;
  const end = start + SEG;
  const opacity = useTransform(progress, [start + 0.01, start + 0.05, end - 0.04, end], [0, 1, 1, 0]);
  const y = useTransform(progress, [start, end], [30, -30]);
  return (
    <motion.div style={{ opacity, y }} className="absolute bottom-[16vh] left-6 lg:left-10 max-w-3xl pointer-events-none">
      <span className="text-[11px] tracking-[0.45em] uppercase text-[#c5a059]">{chapter.label}</span>
      <h2 className="mt-4 font-serif-display font-medium text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-[#f0f4f2]">
        {chapter.title}
      </h2>
    </motion.div>
  );
};

export const ArrivalJourney = () => {
  const { t } = useLang();
  const { setIntensity } = useAmbience();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const [idx, setIdx] = useState(0);
  const [showFinal, setShowFinal] = useState(false);

  useMotionValueEvent(scrollYProgress, "change", (v) => {
    setIdx(Math.min(4, Math.floor(v / SEG)));
    setShowFinal(v >= FINAL_START - 0.03);
    setIntensity(0.5 + 0.5 * Math.min(v / FINAL_START, 1));
  });

  const hintOpacity = useTransform(scrollYProgress, [0, 0.05], [1, 0]);
  const finalOpacity = useTransform(scrollYProgress, [FINAL_START, 0.93], [0, 1]);
  const finalY = useTransform(scrollYProgress, [FINAL_START, 0.95], [50, 0]);
  const chaptersOpacity = useTransform(scrollYProgress, [FINAL_START - 0.03, FINAL_START + 0.02], [1, 0]);

  return (
    <section ref={ref} id="top" data-testid="arrival-journey" className="relative h-[400vh] sm:h-[520vh]">
      <div className="sticky top-0 h-[100svh] overflow-hidden bg-[#050706]">
        {FRAMES.map((frame, i) => (
          <Frame key={i} i={i} frame={frame} progress={scrollYProgress} active={idx === i} />
        ))}

        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          {STEAM.map((p, i) => (
            <span
              key={i}
              className="steam-particle"
              style={{
                left: p.left, width: p.size, height: p.size,
                animationDuration: `${p.duration}s`, animationDelay: `${p.delay}s`,
                "--steam-opacity": p.opacity, "--steam-drift": `${p.drift}px`,
              }}
            />
          ))}
        </div>

        <motion.div style={{ opacity: chaptersOpacity }}>
          {t.arrival.chapters.map((c, i) => (
            <ChapterText key={i} i={i} chapter={c} progress={scrollYProgress} />
          ))}
        </motion.div>

        <motion.div
          style={{ opacity: chaptersOpacity }}
          data-testid="arrival-progress-rail"
          className="absolute right-6 lg:right-10 top-1/2 -translate-y-1/2 hidden sm:flex flex-col items-end gap-4 pointer-events-none"
        >
          {t.arrival.chapters.map((c, i) => (
            <div key={i} className="flex items-center gap-3">
              <span
                className={`text-[10px] tracking-[0.3em] uppercase transition-colors duration-500 ${
                  idx === i ? "text-[#c5a059]" : "text-[#8a9690]/40"
                }`}
              >
                0{i + 1}
              </span>
              <span
                className={`block h-px transition-all duration-500 ${
                  idx === i ? "w-10 bg-[#c5a059]" : "w-5 bg-white/20"
                }`}
              />
            </div>
          ))}
        </motion.div>

        <motion.div
          style={{ opacity: hintOpacity }}
          data-testid="arrival-scroll-hint"
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 text-[#f0f4f2]/80 pointer-events-none"
        >
          <span className="text-[10px] tracking-[0.4em] uppercase">{t.arrival.hint}</span>
          <span className="block w-px h-10 bg-gradient-to-b from-[#c5a059] to-transparent animate-float-gentle" />
        </motion.div>

        <motion.div
          style={{ opacity: finalOpacity, y: finalY }}
          data-testid="arrival-final"
          className={`absolute inset-0 flex flex-col justify-center px-6 lg:px-10 ${showFinal ? "" : "pointer-events-none"}`}
        >
          <div className="max-w-7xl mx-auto w-full">
            <div className="flex items-center gap-4 mb-8">
              <span className="h-px w-12 bg-[#c5a059]" />
              <span className="text-[11px] sm:text-xs tracking-[0.4em] uppercase text-[#c5a059]">{t.hero.eyebrow}</span>
            </div>
            <h1 className="font-serif-display font-medium text-[#f0f4f2] leading-[0.95] tracking-tight text-4xl sm:text-5xl lg:text-6xl xl:text-[7rem]">
              <span className="block">{t.hero.line1}</span>
              <span className="block italic gold-shimmer-text pb-2">{t.hero.line2}</span>
            </h1>
            <p className="mt-8 max-w-xl text-base md:text-lg text-[#a8b5ae] font-light leading-relaxed">{t.hero.sub}</p>
            <div className="flex flex-wrap items-center gap-5 mt-12">
              <a
                href={PHONE_TEL}
                data-testid="arrival-call-button"
                className="group relative overflow-hidden bg-[#c5a059] text-[#050706] px-8 py-4 text-xs tracking-[0.2em] uppercase font-medium transition-transform duration-300 hover:-translate-y-0.5"
              >
                <span className="relative z-10 flex items-center gap-3">
                  <Phone size={14} /> {t.hero.call} {PHONE_DISPLAY}
                </span>
                <span className="absolute inset-0 bg-[#dcc286] translate-y-full group-hover:translate-y-0 transition-transform duration-400" />
              </a>
              <a
                href="#services"
                data-testid="arrival-explore-button"
                className="border border-white/25 text-[#f0f4f2] hover:border-[#c5a059] hover:text-[#c5a059] px-8 py-4 text-xs tracking-[0.2em] uppercase transition-colors duration-300"
              >
                {t.hero.explore}
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
